import { State, Action, Selector } from '@ngxs/store';
import { InfoEtapesParcours } from './modeles/infoEtapesParcours';
import { SupportApiService } from './services/support-api.service';
import { AppConfigService } from '../app-config.service';
import { PersonneApiService } from './services/personne-api.service';
import { LoadPreferencesOctroi } from '../octroi/octroi.state';
import { UpdatePreferences, UpdateEmploi, UpdateCoordonnees } from './preferences.state';
import { Router } from '@angular/router';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { PersonneModel } from '../core/modeles/personneModele';
import { InfoDemandeSupportNumerique } from './modeles/info';
import { Injectable } from '@angular/core';

const defaultState = {
  infoDemandeSupportNumerique: {
    page: {
      code: null,
      description: null
    },
    section: {
      code: null,
      description: null
    },
    canal: null
  },
  popup: {
    displayedName: null,
    params: null
  },
  loader: {
    type: null
  }
};

export interface PopupModel {
  displayedName: string;
  params: any;
}

export interface LoaderModel {
  type: string;
}

export interface PersonneStateModel {
  infoDemandeSupportNumerique: InfoDemandeSupportNumerique;
  popup: PopupModel;
  loader: LoaderModel;
}

export class ModifierParcours {
  static type = '[App] ModifierParcours';
  constructor(public codeSection, public codePage) {}
}

export class ShowPopup {
  static type = '[App] ShowPopup';
  constructor(public popupName, public params?) {}
}

export class HidePopup {
  static type = '[App] HidePopup';
}

export class LoadPersonne {
  static type = '[App] LoadPersonne';
}

export class UpdateClickTelAssuranceHab {
  static type = '[App] UpdateClickTelAssuranceHab';
}

export class UpdateClickTelAssurancePret {
  static type = '[App] UpdateClickTelAssurancePret';
}

export class ShowLoader {
  static type = '[App] ShowLoader';
  constructor(public type?) {}
}

export class HideLoader {
  static type = '[App] HideLoader';
}

export class TriggerErrorPage {
  static type = '[App] TriggerErrorPage';
}

@Injectable()
@State<PersonneStateModel>({
  name: 'personne',
  defaults: defaultState
})
export class PersonneState {
  constructor(
    private readonly _supportApiService: SupportApiService,
    private readonly _personneApiService: PersonneApiService,
    private readonly _appConfigService: AppConfigService,
    private readonly _router: Router
  ) {}

  @Selector()
  static popup(state: PersonneStateModel) {
    return state.popup;
  }

  static detectCanal() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      ? 'Mobile'
      : 'Web';
  }

  static IS_IPAD() {
    return navigator.userAgent.match(/iPad/i) != null;
  }

  static IS_IPHONE() {
    return !this.IS_IPAD() && (navigator.userAgent.match(/iPhone/i) != null || navigator.userAgent.match(/iPod/i) != null);
  }

  static IS_IOS() {
    return this.IS_IPAD() || this.IS_IPHONE();
  }

  static IS_ANDROID() {
    return !this.IS_IOS && navigator.userAgent.match(/android/i) != null;
  }

  @Action(ModifierParcours)
  modifierParcours({ patchState }, { codeSection, codePage }: ModifierParcours) {
    const langue = this._appConfigService.configData.contexteEnvironnement.langue;
    const infoDemandeSupport = {
      page: {
        code: InfoEtapesParcours[codeSection][codePage].codePage,
        description:
          langue === 'fr'
            ? InfoEtapesParcours[codeSection][codePage].descriptionPage.fr
            : InfoEtapesParcours[codeSection][codePage].descriptionPage.en
      },
      section: {
        code: codeSection,
        description:
          langue === 'fr'
            ? InfoEtapesParcours[codeSection].descriptionSection.fr
            : InfoEtapesParcours[codeSection].descriptionSection.en
      },
      canal: PersonneState.detectCanal()
    };
    return this._supportApiService.updateInfoDemandeSupportNumerique(infoDemandeSupport).subscribe(() => {
      patchState({
        infoDemandeSupportNumerique: infoDemandeSupport
      });
    });
  }

  @Action(ShowPopup)
  showPopup({ patchState }, { popupName, params }: ShowPopup) {
    patchState({
      popup: {
        displayedName: popupName,
        params: params
      }
    });
    disableBodyScroll();
  }

  @Action(HidePopup)
  hidePopup({ patchState }) {
    patchState({
      popup: {
        displayedName: null,
        params: null
      }
    });
    clearAllBodyScrollLocks();
  }

  @Action(LoadPersonne)
  async loadPersonne({ dispatch }) {
    const personne = await this._personneApiService.getPersonne().toPromise();
    const preferences = personne.preference;

    this._appConfigService.configData.personne = personne;

    dispatch(new UpdatePreferences(preferences));
    dispatch(new UpdateEmploi(personne.emploi));
    dispatch(new UpdateCoordonnees(personne.coordonnees));
    dispatch(new LoadPreferencesOctroi(preferences));
  }

  @Action(UpdateClickTelAssuranceHab)
  updateClickTelAssuranceHab() {
    return this._supportApiService.updateClickTelAssuranceHab();
  }

  @Action(UpdateClickTelAssurancePret)
  updateClickTelAssurancePret() {
    return this._supportApiService.updateClickTelAssurancePret();
  }

  @Action(ShowLoader)
  showLoader({ patchState }, { type }: ShowLoader) {
    patchState({
      loader: {
        type: type
      }
    });
  }

  @Action(HideLoader)
  hideLoader({ patchState }) {
    patchState({
      loader: {
        type: null
      }
    });
  }

  @Action(TriggerErrorPage)
  triggerErrorPage(_context) {
    return this._router.navigateByUrl('/erreur');
  }
}
